import { ChangeEvent, useState } from 'react';

import {
  countShopsByText,
  searchShopsByText,
} from '@/api/fetch/marketplace-api';
import {
  LocationRequest,
  MatchParams,
  PaginationParams,
  SearchUsersByTitleRequest,
} from '@/api/gen-protos/discovery_pb';
import { LIMIT } from '@/lib/constants';
import { categories } from '@/lib/helpers';
import { mapUserResponseToIUserData } from '@/modules/core/mappings';
import { SelectedCategory } from '@/modules/core/store/store';
import { IUserData } from '@/modules/core/types';

export interface PaginationHookProps {
  initialDataShopList: IUserData[];
  id: string;
  countShops: number;
}

// custom hooks to handle infinite pagination
export const useUsersPaginationHook = ({
  initialDataShopList,
  countShops,
}: PaginationHookProps) => {
  const [page, setpage] = useState<number>(initialDataShopList ? 1 : 0);
  const [params, setParams] = useState<{
    title?: string;
    delegation?: string;
    category?: { id: string };
    subCategory?: { id: string };
    governorate?: string;
  }>({});
  const [data, setData] = useState<IUserData[]>(initialDataShopList);
  const [countData, setCountData] = useState<number>(countShops || 0);

  const updateParamsState = (name: string, value: string) => {
    setParams((p) => ({ ...p, [name]: value }));
  };

  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>(
    {}
  );

  const handleCategory = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === '') {
      setSelectedCategory({});
      setParams((prev) => ({
        ...prev,
        category: undefined,
      }));
    }
    setpage(0);

    const newSelectedCategory =
      categories.find((c) => c.id === e.target.value) || null;
    if (newSelectedCategory !== null) {
      setSelectedCategory(newSelectedCategory!);
      setParams((prev) => ({
        ...prev,
        category: { id: newSelectedCategory.id },
      }));
    }
  };

  const loadMoreShopsList = async (init: boolean) => {
    // const prevData: IUserData[] = [];
    if (init) {
      // prevData = [...data];
      setData([]);
      setpage(0);
      setCountData(0);
    }
    try {
      const { title, delegation, category, subCategory, governorate } = params;
      const searchShopRequest = new SearchUsersByTitleRequest();
      if (title) searchShopRequest.setTitle(`${title}`);

      if (delegation) {
        const location = new LocationRequest();
        location.setDelegation(delegation);
        if (governorate) location.setGovernorate(governorate);

        searchShopRequest.setLocation(location);
      }
      let catId;
      if (category && !subCategory) {
        catId = category.id;
      } else if (subCategory) {
        catId = subCategory.id;
      }
      if (catId) searchShopRequest.setSubcategoryid(catId);

      const paginationParams = new PaginationParams();

      paginationParams.setLimit(LIMIT);
      paginationParams.setSkip(init ? 0 : LIMIT * page);

      const matchParams = new MatchParams();
      matchParams.setPaginationparams(paginationParams);

      searchShopRequest.setMatchparams(matchParams);
      const [shops, count] = await Promise.all([
        searchShopsByText('', searchShopRequest),
        countShopsByText(searchShopRequest),
      ]);

      if (init) {
        setData(shops.map((shop) => mapUserResponseToIUserData(shop)));
      } else {
        setData((prev) => [
          ...prev,
          ...shops.map((shop) => mapUserResponseToIUserData(shop)),
        ]);
      }
      setCountData(count.getCount());
      setpage((p) => p + 1);
    } catch (err) {
      // Error loading more shops...
      // if (init) setData(prevData);
    }
  };
  const resetParams = () => {
    setParams({});
    setCountData(0);
  };

  return {
    data,
    loadMoreShopsList,
    updateParamsState,
    params,
    page,
    resetParams,
    selectedCategory,
    handleCategory,
    countData,
  };
};
